import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import ClearTeamAssistAnswer from 'actions/team_assist/clear_team_assist_answer';
import FetchTeamAssistAnswer from 'actions/team_assist/fetch_team_assist_answer';
import { TeamAssistV2AnswerStatus, TeamAssistV2ErrorCode } from 'actions/team_assist_v2/lib/team_assist_v2_answer';
import { ButtonTypes } from 'components/common/button';
import SafeHtml from 'components/common/safe_html';
import Tooltip from 'components/common/tooltip';
import {
  AssistantActions,
  AssistantContent,
  AssistantMessageWrapper,
  AssistantRow,
  CannedResponsePrompt,
  Citations,
  CopyButton,
  copyAnswerToClipboard,
  ErrorIcon,
  ErrorRetryButton,
  ErrorRow,
  ErrorText,
  formatMessageContent,
  SuggestedResponseActions,
  SuggestedResponseContent,
  ThumbsFeedback,
  UseButton,
  UserBubble,
  UserBubbleSpacer,
  UserMessageContent,
  UserRow,
} from 'components/customer/team_assist/team_assist_message_parts';
import sanitizeTeamAssistHtml from 'components/customer/team_assist/lib/sanitize_team_assist_html';
import { TeamAssistLoadingDots } from 'components/customer/team_assist/team_assist_shared';
import {
  renderCallouts,
  stripCitationHandles,
  toTeamAssistCitation,
} from 'components/customer/team_assist_v2/team_assist_v2_answer_text';
import TeamAssistV2ProposalCard from 'components/customer/team_assist_v2/team_assist_v2_proposal_card';
import TeamAssistV2ToolCard, { toolCardHtml } from 'components/customer/team_assist_v2/team_assist_v2_tool_card';
import { useExecuteAction } from 'components/hooks/connect_hooks';

export function TeamAssistV2Turn({ canRetry, canRun, cdnHost, onRate, onReject, onRetry, onRun, onUse, orgId, turn }) {
  const { answer, displayedMessage, question } = turn;
  const isError = Boolean(answer) && answer.status === TeamAssistV2AnswerStatus.error;
  const followUps = turn.followUps || [];
  const answerProps = {
    canRun,
    cdnHost,
    isLive: Boolean(turn.live),
    onReject: toolUseId => onReject({ toolUseId, turnId: turn.id }),
    onRun: (toolUseId, input) => onRun({ input, toolUseId, turnId: turn.id }),
    onUse,
    orgId,
  };

  return (
    <>
      <UserRow data-aid="teamAssistV2UserMessage">
        <UserBubbleSpacer />
        <QuestionContent>
          {displayedMessage ? (
            <CannedResponsePrompt hasError={isError} label={formatMessageContent(displayedMessage)} prompt={question} />
          ) : (
            <UserBubble hasError={isError}>
              <SafeHtml html={formatMessageContent(question)} />
            </UserBubble>
          )}
        </QuestionContent>
      </UserRow>

      <AnswerRow
        {...answerProps}
        answer={answer}
        onRate={rating => onRate({ messageId: answer.messageId, rating, turnId: turn.id })}
        onRetry={canRetry ? () => onRetry(turn) : null}
      />
      {/* An answer that followed an action the agent ran has nothing to retry:
          the action already ran. */}
      {followUps.map((followUp, index) => (
        <AnswerRow
          {...answerProps}
          answer={followUp}
          key={index}
          onRate={rating => onRate({ messageId: followUp.messageId, rating, turnId: turn.id })}
          onRetry={null}
        />
      ))}
    </>
  );
}

TeamAssistV2Turn.propTypes = {
  canRetry: PropTypes.bool,
  /** Whether a proposed action can run now: nothing streams, loads, or clears. */
  canRun: PropTypes.bool,
  /** The public-asset CDN origin answer images may load from. */
  cdnHost: PropTypes.string,
  onRate: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onRetry: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  onUse: PropTypes.func.isRequired,
  /** The org whose shared uploads answer images may load from. */
  orgId: PropTypes.string,
  turn: PropTypes.shape({
    answer: PropTypes.object,
    displayedMessage: PropTypes.string,
    followUps: PropTypes.arrayOf(PropTypes.object),
    id: PropTypes.string,
    live: PropTypes.bool,
    question: PropTypes.string,
  }).isRequired,
};

export default React.memo(TeamAssistV2Turn);

// One answer in the exchange: the first, or one that followed an action the
// agent ran. Retry shows on a failed answer only when `onRetry` is given.
function AnswerRow({ answer, canRun, cdnHost, isLive, onRate, onReject, onRetry, onRun, onUse, orgId }) {
  const { i18n, t } = useTranslation();
  const executeAction = useExecuteAction();
  const [previewAnswerId, setPreviewAnswerId] = useState(null);
  const isDone = Boolean(answer) && answer.status === TeamAssistV2AnswerStatus.done;
  const isError = Boolean(answer) && answer.status === TeamAssistV2AnswerStatus.error;
  const isStreaming = Boolean(answer) && answer.status === TeamAssistV2AnswerStatus.streaming;
  const blocks = (answer && answer.blocks) || [];
  const hasShownBlocks = blocks.some(isShown);
  const citations = isDone ? (answer.citations || []).map(toTeamAssistCitation) : [];
  const htmlOptions = { orgId, cdnHost };
  const cardOptions = { locale: i18n.resolvedLanguage || i18n.language, t };

  return (
    <AssistantRow data-aid="teamAssistV2AssistantMessage">
      <AssistantMessageWrapper>
        <AssistantContent>
          {blocks.map(block => (
            <AnswerBlock
              block={block}
              canRun={canRun}
              htmlOptions={htmlOptions}
              isDone={isDone}
              isLive={isLive}
              key={block.index}
              onReject={() => onReject(block.toolUseId)}
              onRun={input => onRun(block.toolUseId, input)}
              onUse={html => onUse({ html, messageId: answer.messageId })}
            />
          ))}

          {isStreaming && isWorking(blocks) && <TeamAssistLoadingDots />}

          {isError && (
            <ErrorRow data-aid="teamAssistV2Error" role="alert">
              <ErrorIcon className="fa fa-exclamation-circle" />
              <ErrorText>{errorMessage(t, answer.error)}</ErrorText>
              {onRetry && (
                <ErrorRetryButton data-aid="teamAssistV2RetryButton" onClick={onRetry} type="button">
                  <i aria-hidden="true" className="fa fa-repeat" /> {t('Retry')}
                </ErrorRetryButton>
              )}
            </ErrorRow>
          )}

          {isDone && (
            <>
              <Citations
                citations={citations}
                onCitationClick={answerId => {
                  setPreviewAnswerId(answerId);
                  executeAction(FetchTeamAssistAnswer, { answerId });
                }}
                onClosePreview={() => {
                  setPreviewAnswerId(null);
                  executeAction(ClearTeamAssistAnswer);
                }}
                previewAnswerId={previewAnswerId}
              />
              <AssistantActions>
                {hasShownBlocks && (
                  <CopyAnswerButton
                    aidSuffix="Answer"
                    html={answerHtml(blocks, htmlOptions, cardOptions)}
                    label={t('Copy response')}
                  />
                )}
                {answer.messageId && (
                  <ThumbsFeedback onRate={onRate} ratingValue={answer.rating ? answer.rating.value : undefined} />
                )}
              </AssistantActions>
            </>
          )}
        </AssistantContent>
      </AssistantMessageWrapper>
    </AssistantRow>
  );
}

AnswerRow.propTypes = {
  answer: PropTypes.object,
  canRun: PropTypes.bool,
  cdnHost: PropTypes.string,
  isLive: PropTypes.bool,
  onRate: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onRetry: PropTypes.func,
  onRun: PropTypes.func.isRequired,
  onUse: PropTypes.func.isRequired,
  orgId: PropTypes.string,
};

function AnswerBlock({ block, canRun, htmlOptions, isDone, isLive, onReject, onRun, onUse }) {
  const { t } = useTranslation();
  const html = blockHtml(block, htmlOptions);

  if (block.type === 'text') {
    return (
      <AnswerContent>
        <SafeHtml html={html} />
      </AnswerContent>
    );
  }

  if (block.type === 'suggested_reply') {
    return (
      <ReplyCard data-aid="teamAssistV2SuggestedReply">
        <ReplyLabel>{t('Suggested reply')}</ReplyLabel>
        <AnswerContent>
          <SafeHtml html={html} />
        </AnswerContent>
        {isDone && (
          <SuggestedResponseActions>
            <UseReplyButton onUse={() => onUse(html)} />
            <CopyAnswerButton aidSuffix="Reply" html={html} label={t('Copy suggested reply')} />
          </SuggestedResponseActions>
        )}
      </ReplyCard>
    );
  }

  if (block.type === 'tool_use' && block.proposal) {
    return <TeamAssistV2ProposalCard block={block} canRun={canRun} isLive={isLive} onReject={onReject} onRun={onRun} />;
  }

  if (block.type === 'tool_use') {
    return <TeamAssistV2ToolCard execution={block.execution} />;
  }

  return null;
}

AnswerBlock.propTypes = {
  block: PropTypes.shape({
    execution: PropTypes.object,
    index: PropTypes.number,
    proposal: PropTypes.object,
    text: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
  canRun: PropTypes.bool,
  htmlOptions: PropTypes.shape({ cdnHost: PropTypes.string, orgId: PropTypes.string }).isRequired,
  isDone: PropTypes.bool,
  isLive: PropTypes.bool,
  onReject: PropTypes.func.isRequired,
  onRun: PropTypes.func.isRequired,
  onUse: PropTypes.func.isRequired,
};

function UseReplyButton({ onUse }) {
  const { t } = useTranslation();
  const [isUsed, setIsUsed] = useState(false);

  return (
    <UseButton
      aria-label={t('Use suggested reply')}
      buttonType={ButtonTypes.PRIMARY}
      data-aid="teamAssistV2UseButton"
      disabled={isUsed}
      isSmall
      onClick={() => {
        setIsUsed(true);
        onUse();
      }}
    >
      {t('Use')}
    </UseButton>
  );
}

UseReplyButton.propTypes = {
  onUse: PropTypes.func.isRequired,
};

function CopyAnswerButton({ aidSuffix, html, label }) {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    if (!isCopied) {
      return undefined;
    }
    const timer = setTimeout(() => setIsCopied(false), COPIED_FEEDBACK_MS);
    return () => clearTimeout(timer);
  }, [isCopied]);

  const onCopied = copied => copied && setIsCopied(true);
  const copy = () => copyAnswerToClipboard(html).then(onCopied);

  return (
    <Tooltip delay={250} message={isCopied ? t('Copied') : t('Copy')} position="bottom" style={{ padding: '5px 10px' }}>
      <CopyButton
        aria-label={isCopied ? t('Copied') : label}
        data-aid={`teamAssistV2Copy${aidSuffix}Button`}
        isCopied={isCopied}
        onClick={copy}
        type="button"
      >
        <i aria-hidden="true" className={isCopied ? 'fa fa-check' : 'fa fa-copy'} />
      </CopyButton>
    </Tooltip>
  );
}

CopyAnswerButton.propTypes = {
  aidSuffix: PropTypes.string.isRequired,
  html: PropTypes.string,
  label: PropTypes.string.isRequired,
};

const COPIED_FEEDBACK_MS = 2000;
const SHOWN_BLOCK_TYPES = ['text', 'suggested_reply'];

// The whole answer for Copy: what it says, and the cards it shows where the
// model called their tools.
function answerHtml(blocks, htmlOptions, cardOptions) {
  return blocks
    .map(block => {
      if (isShown(block)) {
        return blockHtml(block, htmlOptions);
      }
      return block.type === 'tool_use' ? toolCardHtml(block.execution, cardOptions) : '';
    })
    .filter(Boolean)
    .join('<br /><br />');
}

function isShown(block) {
  return SHOWN_BLOCK_TYPES.includes(block.type);
}

// Still working: nothing to read yet, or a tool is running after what's shown.
function isWorking(blocks) {
  return !blocks.some(isShown) || blocks[blocks.length - 1].type === 'tool_use';
}

/**
 * A block's HTML as the panel shows it, Use inserts it, and Copy copies it:
 * citation handles dropped, callouts turned into the panel's element (none in
 * drafts), then cleaned to the Answer editor's set.
 */
function blockHtml(block, htmlOptions) {
  const inDraft = block.type === 'suggested_reply';
  const text = renderCallouts(stripCitationHandles(block.text), { inDraft });
  return sanitizeTeamAssistHtml(formatMessageContent(text), { ...htmlOptions, allowCallouts: !inDraft });
}

// glados's own message isn't translated, so a server error shows the
// fallback below, in the agent's language.
function errorMessage(t, error) {
  if (error && error.code === TeamAssistV2ErrorCode.interrupted) {
    return t('This answer stopped partway through. Try asking again.');
  }
  if (error && error.code === TeamAssistV2ErrorCode.network) {
    return t("We couldn't reach Team Assist. Check your connection and try again.");
  }
  return t("Team Assist couldn't answer. Try asking again.");
}

const ReplyCard = styled.div`
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 8px;
  margin: 8px 0;
  padding: 8px 12px;
`;

const ReplyLabel = styled.div`
  color: ${p => p.theme.colors.gray800};
  font-size: var(--font-size-reduced);
  font-weight: ${p => p.theme.fontWeight.medium};
  margin-bottom: 4px;
`;

// V1's question wrapper pads its bottom on top of the row's own margin,
// which leaves a gap before the answer that nothing else in the turn has.
const QuestionContent = styled(UserMessageContent)`
  padding-bottom: 0;
`;

// Answer HTML arrives without its own borders or padding (the sanitizer drops
// them), so tables get the panel's.
const AnswerContent = styled(SuggestedResponseContent)`
  table {
    border-collapse: collapse;
    margin: 4px 0;
  }
  th,
  td {
    border: 1px solid ${p => p.theme.colors.gray300};
    padding: 4px 8px;
  }
`;
